import React, { FC, useCallback, useMemo, useState } from 'react';

import { Box, Button, Checkbox, Flex, Stack, Text } from '@chakra-ui/react';
import { useDisclosure } from '@chakra-ui/hooks';
import { MdChevronRight, MdClose, MdTune } from 'react-icons/md';
import { AnimatePresence, motion } from 'framer-motion';
import { useQuery } from 'react-query';

import { fetchManufacturers } from 'api/manufacturer';
import { fetchWashHubLocations } from 'api/location';
import { fetchAllSKU } from 'api/sku';
import { PURCHASE_ORDER_STATUS_TO_LABEL } from 'constants/purchaseOrder';

import {
  PurchaseOrderFiltersProps,
  PurchaseOrderFiltersValues,
} from './PurchaseOrderFilters.props';

const MotionFlex = motion<any>(Flex);

const animationConfig = {
  top: 0,
  left: 2,
  zIndex: 2,
  color: 'primary.500',
  fontSize: 14,
  fontWeight: '600',
  position: 'absolute',
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.3 },
};

const css = {
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    width: '10px',
    height: '10px',
    background: '#10722450',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#1E9435',
    borderRadius: '24px',
  },
};

type FilterOption = {
  _id: string;
  name: string;
};

type FilterGroup = {
  name: string;
  type: keyof PurchaseOrderFiltersValues;
  data: FilterOption[];
};

const sortData = (data: FilterOption[]) =>
  [...data].sort((a, b) =>
    a.name
      .toLowerCase()
      .localeCompare(b.name.toLowerCase(), 'en', { numeric: true }),
  );

const PurchaseOrderFiltersView: FC<PurchaseOrderFiltersProps> = ({
  values,
  onApplyFilters,
  onClearFilters,
}) => {
  const { isOpen, onClose, onToggle } = useDisclosure();
  const [index, setIndex] = useState(0);
  const [selectedType, setSelectedType] = useState<
    keyof PurchaseOrderFiltersValues | undefined
  >();

  const [localStatus, setLocalStatus] = useState<string[]>(values.status || []);
  const [localManufacturerId, setLocalManufacturerId] = useState<string[]>(
    values.manufacturerId || [],
  );
  const [localDestinationLocationId, setLocalDestinationLocationId] = useState<
    string[]
  >(values.destinationLocationId || []);
  const [localSkuId, setLocalSkuId] = useState<string[]>(values.skuId || []);

  const [appliedStatus, setAppliedStatus] = useState<string[]>(
    values.status || [],
  );
  const [appliedManufacturerId, setAppliedManufacturerId] = useState<string[]>(
    values.manufacturerId || [],
  );
  const [
    appliedDestinationLocationId,
    setAppliedDestinationLocationId,
  ] = useState<string[]>(values.destinationLocationId || []);
  const [appliedSkuId, setAppliedSkuId] = useState<string[]>(
    values.skuId || [],
  );

  const { data: manufacturers = [] } = useQuery(
    ['fetch-manufacturers-filter'],
    () => fetchManufacturers(),
  );
  const { data: washHubs = [] } = useQuery(
    ['fetch-wash-hub-locations-filter'],
    fetchWashHubLocations,
  );
  const { data: skus = [] } = useQuery(['fetch-skus-filter'], () =>
    fetchAllSKU('item', undefined, undefined, undefined, true),
  );

  const filterGroups = useMemo<FilterGroup[]>(
    () => [
      {
        name: 'Status',
        type: 'status',
        data: sortData(
          Object.entries(PURCHASE_ORDER_STATUS_TO_LABEL).map(
            ([value, label]) => ({
              _id: value,
              name: label,
            }),
          ),
        ),
      },
      {
        name: 'Destination Hub',
        type: 'destinationLocationId',
        data: sortData(
          washHubs.map((hub) => ({
            _id: hub._id,
            name: hub.name,
          })),
        ),
      },
      {
        name: 'Manufacturer',
        type: 'manufacturerId',
        data: sortData(
          manufacturers.map((manufacturer) => ({
            _id: manufacturer._id,
            name: manufacturer.name,
          })),
        ),
      },
      {
        name: 'SKU',
        type: 'skuId',
        data: sortData(
          (skus || []).map((sku) => ({
            _id: sku._id,
            name: sku.name,
          })),
        ),
      },
    ],
    [manufacturers, washHubs, skus],
  );

  const isFilters = useMemo(
    () =>
      Boolean(appliedStatus.length) ||
      Boolean(appliedManufacturerId.length) ||
      Boolean(appliedDestinationLocationId.length) ||
      Boolean(appliedSkuId.length),
    [
      appliedStatus,
      appliedManufacturerId,
      appliedDestinationLocationId,
      appliedSkuId,
    ],
  );

  const getLocalSelected = (type?: keyof PurchaseOrderFiltersValues) => {
    if (type === 'status') {
      return localStatus;
    }
    if (type === 'manufacturerId') {
      return localManufacturerId;
    }
    if (type === 'destinationLocationId') {
      return localDestinationLocationId;
    }
    if (type === 'skuId') {
      return localSkuId;
    }
    return [];
  };

  const setLocalSelected = (
    type: keyof PurchaseOrderFiltersValues,
    next: string[],
  ) => {
    if (type === 'status') {
      setLocalStatus(next);
    } else if (type === 'manufacturerId') {
      setLocalManufacturerId(next);
    } else if (type === 'destinationLocationId') {
      setLocalDestinationLocationId(next);
    } else if (type === 'skuId') {
      setLocalSkuId(next);
    }
  };

  const onCancel = () => {
    onClose();
    setLocalStatus(appliedStatus);
    setLocalManufacturerId(appliedManufacturerId);
    setLocalDestinationLocationId(appliedDestinationLocationId);
    setLocalSkuId(appliedSkuId);
  };

  const onApply = () => {
    setAppliedStatus(localStatus);
    setAppliedManufacturerId(localManufacturerId);
    setAppliedDestinationLocationId(localDestinationLocationId);
    setAppliedSkuId(localSkuId);

    onApplyFilters({
      status: localStatus,
      manufacturerId: localManufacturerId,
      destinationLocationId: localDestinationLocationId,
      skuId: localSkuId,
    });
    onClose();
  };

  const onClear = () => {
    setLocalStatus([]);
    setLocalManufacturerId([]);
    setLocalDestinationLocationId([]);
    setLocalSkuId([]);
    setAppliedStatus([]);
    setAppliedManufacturerId([]);
    setAppliedDestinationLocationId([]);
    setAppliedSkuId([]);
    onClearFilters();
  };

  const renderButton = useCallback(
    () => (
      <Button
        px={3}
        mb={5}
        h="40px"
        w="150px"
        bg="#E1EFE3"
        variant="ghost"
        onClick={onToggle}
        justifyContent="space-between"
        rightIcon={<MdTune size="24px" />}>
        Filters
      </Button>
    ),
    [onToggle],
  );

  return (
    <Flex userSelect="none">
      {renderButton()}
      <Box position="relative">
        <AnimatePresence>
          {isOpen && (
            <MotionFlex {...animationConfig}>
              <Flex
                bg="#fff"
                direction="column"
                borderRadius={8}
                overflow="hidden"
                boxShadow="0px 0px 5px 0px rgba(0,0,0,0.1)">
                {filterGroups.map((filter, idx) => (
                  <Flex
                    key={filter.type}
                    cursor="pointer"
                    px={4}
                    py={2.5}
                    maxW="350px"
                    bg={selectedType && idx === index ? '#D8EBDF' : '#fff'}
                    justify="space-between"
                    align="center"
                    onClick={() => {
                      setIndex(idx);
                      setSelectedType(filter.type);
                    }}>
                    <Text>{filter.name}</Text>
                    <MdChevronRight size="20px" />
                  </Flex>
                ))}
              </Flex>
              {selectedType ? (
                <Flex
                  bg="#fff"
                  top={0}
                  right={0}
                  zIndex={1}
                  minW="240px"
                  marginLeft={2}
                  borderRadius={8}
                  align="flex-start"
                  direction="column"
                  boxShadow="0px 0px 5px 0px rgba(0,0,0,0.1)"
                  maxH="350px">
                  <Flex
                    py={2}
                    px={3}
                    direction="column"
                    overflow="auto"
                    css={css}>
                    {filterGroups[index]?.data.map((item) => {
                      const selectedIds = getLocalSelected(selectedType);
                      const isChecked = selectedIds.includes(item._id);

                      return (
                        <Checkbox
                          key={item._id}
                          isChecked={isChecked}
                          onChange={() => {
                            if (isChecked) {
                              setLocalSelected(
                                selectedType,
                                selectedIds.filter((id) => id !== item._id),
                              );
                            } else {
                              setLocalSelected(selectedType, [
                                ...selectedIds,
                                item._id,
                              ]);
                            }
                          }}
                          mb={2}
                          w="100%"
                          colorScheme="primary">
                          <Stack minW="100%" isInline>
                            <Text flex={1} w="130px" noOfLines={1}>
                              {item.name}
                            </Text>
                          </Stack>
                        </Checkbox>
                      );
                    })}
                  </Flex>
                  <Stack alignSelf="center" py={2} px={3} isInline>
                    <Button onClick={onCancel} size="sm" variant="outline">
                      Cancel
                    </Button>
                    <Button size="sm" onClick={onApply}>
                      Apply
                    </Button>
                  </Stack>
                </Flex>
              ) : null}
            </MotionFlex>
          )}
        </AnimatePresence>
      </Box>
      {isFilters && (
        <Button
          bg="#E1EFE3"
          variant="ghost"
          h="40px"
          ml={2}
          rightIcon={<MdClose />}
          onClick={onClear}>
          Clear
        </Button>
      )}
    </Flex>
  );
};

export default PurchaseOrderFiltersView;
